import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  NotificationsBySubscription,
  UntypedNotificationSettingsModel,
} from "@shared/types/common";
import { NotificationHistory } from "@shared/types/localTypes/notifications-center.ts";
import { DateTime } from "luxon";

const defaultHistoryStartDate = DateTime.now()
  .minus({ month: 1 })
  .startOf("day");
const defaultHistoryEndDate = DateTime.now();

export const useNotificationsCenterStore = defineStore(
  "notifications-center",
  () => {
    // State
    const _notificationsBySubscriptions = ref<NotificationsBySubscription[]>(
      [],
    );
    const _notificationsAreLoading = ref<boolean>(true);
    const _historyItems = ref<NotificationHistory[]>([]);
    const _initialHistoryStartDate = ref<DateTime>(defaultHistoryStartDate);
    const _initialHistoryEndDate = ref<DateTime>(defaultHistoryEndDate);
    const _historyStartDate = ref<DateTime>(defaultHistoryStartDate);
    const _historyEndDate = ref<DateTime>(defaultHistoryEndDate);
    const _historyIsLoading = ref<boolean>(true);

    // Getters
    const notificationsBySubscriptions = computed(
      () => _notificationsBySubscriptions.value,
    );
    const notificationsAreLoading = computed(
      () => _notificationsAreLoading.value,
    );
    const historyItems = computed(() => _historyItems.value);
    const initialHistoryStartDate = computed(
      () => _initialHistoryStartDate.value,
    );
    const initialHistoryEndDate = computed(() => _initialHistoryEndDate.value);
    const historyStartDate = computed(() => _historyStartDate.value);
    const historyEndDate = computed(() => _historyEndDate.value);
    const historyIsLoading = computed(() => _historyIsLoading.value);

    // Actions
    const setNotificationsAreLoading = (isLoading: boolean) => {
      _notificationsAreLoading.value = isLoading;
    };

    const setNotificationsBySubscriptions = (
      notifications: NotificationsBySubscription[],
    ) => {
      _notificationsBySubscriptions.value = notifications;
    };

    const updateNotificationsForSubscription = (
      notificationsForSubscription: NotificationsBySubscription | null,
    ) => {
      if (!notificationsForSubscription) {
        return;
      }
      const indexOfItemToUpdate = _notificationsBySubscriptions.value.findIndex(
        (item) =>
          item.subscriptionNumber ===
          notificationsForSubscription.subscriptionNumber,
      );
      if (indexOfItemToUpdate === -1) {
        return;
      }
      _notificationsBySubscriptions.value[indexOfItemToUpdate] =
        notificationsForSubscription;
    };

    const updateNotificationItem = (
      subscriptionNumber: string,
      updatedNotificationItem: UntypedNotificationSettingsModel,
    ) => {
      const indexOfNotificationGroupToUpdate =
        _notificationsBySubscriptions.value.findIndex(
          (item) => item.subscriptionNumber === subscriptionNumber,
        );
      if (indexOfNotificationGroupToUpdate === -1) {
        return;
      }
      _notificationsBySubscriptions.value[indexOfNotificationGroupToUpdate] = {
        ..._notificationsBySubscriptions.value[
          indexOfNotificationGroupToUpdate
        ],
        notifications: [
          ..._notificationsBySubscriptions.value[
            indexOfNotificationGroupToUpdate
          ].notifications.filter(
            (notification) =>
              notification.uuid !== updatedNotificationItem.uuid,
          ),
          updatedNotificationItem,
        ],
      };
    };

    const removeNotificationItem = (
      subscriptionNumber: string,
      uuidToRemove: string,
    ) => {
      const indexOfNotificationGroupToUpdate =
        _notificationsBySubscriptions.value.findIndex(
          (item) => item.subscriptionNumber === subscriptionNumber,
        );
      if (indexOfNotificationGroupToUpdate === -1) {
        return;
      }
      _notificationsBySubscriptions.value[indexOfNotificationGroupToUpdate] = {
        ..._notificationsBySubscriptions.value[
          indexOfNotificationGroupToUpdate
        ],
        notifications: _notificationsBySubscriptions.value[
          indexOfNotificationGroupToUpdate
        ].notifications.filter(
          (notification) => notification.uuid !== uuidToRemove,
        ),
      };
    };

    const setHistoryDates = (startDate: DateTime, endDate: DateTime) => {
      _historyStartDate.value = startDate;
      _historyEndDate.value = endDate;
    };

    const setHistoryItems = (items: NotificationHistory[]) => {
      _historyItems.value = items;
    };

    const setHistoryIsLoading = (isLoading: boolean) => {
      _historyIsLoading.value = isLoading;
    };

    return {
      notificationsBySubscriptions,
      notificationsAreLoading,
      historyItems,
      initialHistoryStartDate,
      initialHistoryEndDate,
      historyStartDate,
      historyEndDate,
      historyIsLoading,
      setNotificationsAreLoading,
      setNotificationsBySubscriptions,
      updateNotificationsForSubscription,
      updateNotificationItem,
      removeNotificationItem,
      setHistoryDates,
      setHistoryItems,
      setHistoryIsLoading,
    };
  },
);
